import {useEffect, useMemo} from 'react';
import {create} from 'zustand';

import {
  RampIdType,
  useEntityFeatureFlagsQuery,
} from '../../../generated/graphql';
import {useProjectParams} from '../../../pages/ProjectPage/useProjectParams';
import {type FlagMap, toFlagMap} from './flagMap';

/**
 * Cache for `useEntityFeatureFlagsQuery`, keyed by `rampIDType`. `entityName`
 * is stored alongside the flags so swapping entities atomically replaces
 * stale values instead of flashing the previous entity's flags. Flag lists
 * are precomputed into rampKey -> boolean maps at hydrate time so readers
 * are a single Record lookup + CI-bypass branch.
 */
export type EntityFlagsState = {
  entityName: string | null;
  flagsByEntityName: FlagMap | null;
  flagsByEntityId: FlagMap | null;
};

const INITIAL_ENTITY_FLAGS_STATE: EntityFlagsState = {
  entityName: null,
  flagsByEntityName: null,
  flagsByEntityId: null,
};

// Reader hooks live in `useRampFlagStore.ts`; export the store so they can
// subscribe to it directly.
export const useEntityFlagsStore = create<EntityFlagsState>()(
  () => INITIAL_ENTITY_FLAGS_STATE
);

/**
 * Fires `useEntityFeatureFlagsQuery` for each supported `rampIDType` and
 * syncs results into the store. `entityName: undefined` skips the queries
 * and readers fall back to defaults.
 */
export function useInitializeEntityFlagsStore() {
  // We are using the url to determine entity name sice a user can only visit
  // one entity at a time. In reports, we support multiple runSets, so be careful.
  const {entityName} = useProjectParams();

  const {data: dataEntityName} = useEntityFeatureFlagsQuery({
    variables: {teamName: entityName, rampIDType: RampIdType.EntityName},
    skip: entityName == null,
  });
  const {data: dataEntityId} = useEntityFeatureFlagsQuery({
    variables: {teamName: entityName, rampIDType: RampIdType.EntityId},
    skip: entityName == null,
  });

  const flagsByEntityName = useMemo(
    () => toFlagMap(dataEntityName?.entity?.featureFlags ?? null),
    [dataEntityName]
  );
  const flagsByEntityId = useMemo(
    () => toFlagMap(dataEntityId?.entity?.featureFlags ?? null),
    [dataEntityId]
  );

  // This is a valid Effect: it synchronizes React hook outputs into the
  // external Zustand store after render.
  // https://react.dev/reference/react/useEffect
  useEffect(() => {
    useEntityFlagsStore.setState({
      entityName,
      flagsByEntityName,
      flagsByEntityId,
    });
  }, [entityName, flagsByEntityName, flagsByEntityId]);
}
